import { type FC, useMemo } from "react";
import { SidebarGroup, SidebarGroupContent, SidebarGroupLabel } from "@capacit-com/lara-law-shared-ui";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import {
  Legislation,
  LegislationMetadata,
} from "@/models/legislation/Legislation";
import { WorkspaceDocument } from "@/models/workspace/Workspace";
import {
  WorkspaceDocumentMetadata,
  DocumentMetadataType,
} from "@/models/metadata/metadata";
import useTenant from "@/hooks/useTenant";
import { dateValue } from "@/utils/legislationMetadataUtils";
import { ArvidMetadata } from "@/models/legislation/metadata/Arvid";
import { DomsdatabasenMetadata } from "@/models/legislation/metadata/Domsdatabasen";
import { EurLexMetadata } from "@/models/legislation/metadata/EURLex";
import { RetsinformationMetadata } from "@/models/legislation/metadata/Retsinformation";
import {
  MetadataItem,
  StandardMetadataTypes,
} from "@/pages/legislation/components/metadata/dialog-items/MetadataItem";
import { RetsinformationMetadataItem } from "@/models/legislation/metadata/Retsinformation";
import { MinistryRessortHint } from "@/pages/legislation/components/MinistryRessortHint";
import {
  arvidLabelMap,
  domsdatabasenLabelMap,
  eurLexLabelMap,
} from "@/utils/legislationMetadataUtils";
import { DataSource, normalizeDataSource } from "@/config/datasources";

type DocumentMetadataTabProps = {
  legislation?: Legislation | null;
  workspaceDocument?: WorkspaceDocument | null;
};

/** Convert workspace document metadata value to the type used by MetadataItem. */
function workspaceMetadataToDisplayValue(
  meta: DocumentMetadataType
): StandardMetadataTypes {
  if (meta.stringValue != null && meta.stringValue !== "") return meta.stringValue;
  if (meta.numberValue != null) return meta.numberValue;
  if (meta.booleanValue != null) return meta.booleanValue;
  if (meta.dateTimeValues?.length) {
    const first = meta.dateTimeValues[0];
    const dateStr =
      typeof first === "string"
        ? first
        : first instanceof Date
          ? first.toISOString()
          : String(first);
    return dateValue(dateStr);
  }
  if (meta.selectValues?.length) return meta.selectValues;
  return null;
}

/** Sidebar content for workspace document metadata (tenant-defined metadata types). */
const WorkspaceMetadataSidebarContent: FC<{
  workspaceDocument: WorkspaceDocument;
}> = ({ workspaceDocument }) => {
  const tenantQuery = useTenant().useQueryCurrent();

  const items = useMemo(() => {
    const tenantTypes = tenantQuery.data?.tenantDocumentMetadataList ?? [];
    const docMetaList = workspaceDocument.documentMetadataList ?? [];
    return tenantTypes.map((tenantType) => {
      const docMeta = docMetaList.find(
        (d: WorkspaceDocumentMetadata) =>
          d.name === tenantType.name ||
          (tenantType.externalPropertyName &&
            d.externalPropertyName === tenantType.externalPropertyName)
      );
      const displayValue = docMeta
        ? workspaceMetadataToDisplayValue(docMeta.metadata)
        : null;
      return {
        name: tenantType.name,
        value: displayValue,
      };
    });
  }, [tenantQuery.data, workspaceDocument.documentMetadataList]);

  const itemsWithValue = items.filter((item) => item.value != null);

  return (
    <div className="flex flex-col gap-3">
      {itemsWithValue.map((item) => (
        <MetadataItem
          key={item.name}
          title={item.name}
          metadata={item.value ?? undefined}
        />
      ))}
      {items.length === 0 && (
        <div className="text-center text-sm text-muted-foreground py-4">
          Ingen metadata defineret for denne dokumenttype. Administrer metadata
          under Indstillinger → Metadata.
        </div>
      )}
      {items.length > 0 && itemsWithValue.length === 0 && (
        <div className="text-center text-sm text-muted-foreground py-4">
          Ingen metadata angivet for dette dokument.
        </div>
      )}
    </div>
  );
};

// Sidebar-specific metadata content components
const ArvidSidebarContent: FC<{ metadata: ArvidMetadata }> = ({ metadata }) => {
  const compactFields: Array<{
    key: keyof ArvidMetadata;
    label: string;
    value: StandardMetadataTypes;
  }> = [
    {
      key: "journalNumber",
      label: arvidLabelMap.journalNumber,
      value: metadata.journalNumber ?? null,
    },
    {
      key: "authority",
      label: arvidLabelMap.authority,
      value: metadata.authority?.name ?? null,
    },
    {
      key: "rulingOn",
      label: arvidLabelMap.rulingOn,
      value: metadata.rulingOn ?? null,
    },
    {
      key: "publishedOn",
      label: arvidLabelMap.publishedOn,
      value: metadata.publishedOn ?? null,
    },
    {
      key: "labels",
      label: arvidLabelMap.labels,
      value: metadata.labels ?? null,
    },
    {
      key: "nullified",
      label: arvidLabelMap.nullified,
      value: metadata.nullified ?? null,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {metadata.summary && (
        <MetadataItem
          title={arvidLabelMap.summary}
          metadata={metadata.summary}
          isLongText={true}
        />
      )}
      <div className="flex flex-col gap-3">
        {compactFields.map(({ key, label, value }) => (
          <MetadataItem key={key} title={label} metadata={value} />
        ))}
      </div>
    </div>
  );
};

const DomsdatabasenSidebarContent: FC<{ metadata: DomsdatabasenMetadata }> = ({
  metadata,
}) => {
  const compactFields: Array<{
    key: keyof DomsdatabasenMetadata;
    label: string;
    value: StandardMetadataTypes;
  }> = [
    {
      key: "courtCaseNumber",
      label: domsdatabasenLabelMap.courtCaseNumber,
      value: metadata.courtCaseNumber ?? null,
    },
    {
      key: "officeName",
      label: domsdatabasenLabelMap.officeName,
      value: metadata.officeName ?? null,
    },
    {
      key: "instance",
      label: domsdatabasenLabelMap.instance,
      value: metadata.instance ?? null,
    },
    {
      key: "caseType",
      label: domsdatabasenLabelMap.caseType,
      value: metadata.caseType ?? null,
    },
    {
      key: "verdictStatus",
      label: domsdatabasenLabelMap.verdictStatus,
      value: metadata.verdictStatus ?? null,
    },
    {
      key: "caseSubjects",
      label: domsdatabasenLabelMap.caseSubjects,
      value: metadata.caseSubjects ?? null,
    },
    {
      key: "caseTags",
      label: domsdatabasenLabelMap.caseTags,
      value: metadata.caseTags ?? null,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {metadata.headline && (
        <MetadataItem
          title={domsdatabasenLabelMap.headline}
          metadata={metadata.headline}
          isLongText={true}
        />
      )}
      <div className="flex flex-col gap-3">
        {compactFields.map(({ key, label, value }) => (
          <MetadataItem key={key} title={label} metadata={value} />
        ))}
      </div>
    </div>
  );
};

const EURLexSidebarContent: FC<{ metadata: EurLexMetadata }> = ({
  metadata,
}) => {
  const compactFields: Array<{
    key: keyof EurLexMetadata;
    label: string;
    value: StandardMetadataTypes;
  }> = [
    {
      key: "celexNumber",
      label: eurLexLabelMap.celexNumber,
      value: metadata.celexNumber ?? null,
    },
    { key: "eli", label: eurLexLabelMap.eli, value: metadata.eli ?? null },
    {
      key: "documentType",
      label: eurLexLabelMap.documentType,
      value: metadata.documentType ?? null,
    },
    {
      key: "processedAt",
      label: eurLexLabelMap.processedAt,
      value: metadata.processedAt ?? null,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {metadata.title && (
        <MetadataItem
          title={eurLexLabelMap.title}
          metadata={metadata.title}
          isLongText={true}
        />
      )}
      <div className="flex flex-col gap-3">
        {compactFields.map(({ key, label, value }) => (
          <MetadataItem key={key} title={label} metadata={value} />
        ))}
      </div>
    </div>
  );
};

const RetsinformationSidebarContent: FC<{
  metadata: RetsinformationMetadata;
}> = ({ metadata }) => {
  const metadataItems = metadata.metadata ?? [];
  return (
    <div className="flex flex-col gap-4">
      {metadata.shortName && (
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-foreground">
            {metadata.shortName}
          </p>
        </div>
      )}
      {metadata.ressort && (
        <>
          <p className="text-xs text-muted-foreground">{metadata.ressort}</p>
          <MinistryRessortHint ressort={metadata.ressort} />
        </>
      )}
      <div className="flex flex-col gap-3">
        {metadataItems.map((item: RetsinformationMetadataItem) => {
          if (/<[a-z][\s\S]*>/i.test(item.displayValue ?? "")) {
            return (
              <div key={item.displayName}>
                <p className="text-muted-foreground text-sm">
                  {item.displayName}
                </p>
                <div
                  className="text-foreground text-sm whitespace-normal wrap-break-words"
                  dangerouslySetInnerHTML={{
                    __html: item.displayValue ?? "",
                  }}
                />
              </div>
            );
          }
          return (
            <div key={item.displayName}>
              <p className="text-muted-foreground text-sm">
                {item.displayName}
              </p>
              <div
                className="text-foreground text-sm whitespace-normal wrap-break-words"
                dangerouslySetInnerHTML={{
                  __html: item.displayValue ?? "",
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Component registry for sidebar content by datasource.
 * Uses DataSource enum for type-safe lookups.
 */
const SIDEBAR_CONTENT_REGISTRY: Partial<
  Record<DataSource, FC<{ metadata: LegislationMetadata }>>
> = {
  [DataSource.Arvid]: ({ metadata }) => (
    <ArvidSidebarContent metadata={metadata as ArvidMetadata} />
  ),
  [DataSource.Domsdatabasen]: ({ metadata }) => (
    <DomsdatabasenSidebarContent metadata={metadata as DomsdatabasenMetadata} />
  ),
  [DataSource.Eurlex]: ({ metadata }) => (
    <EURLexSidebarContent metadata={metadata as EurLexMetadata} />
  ),
  [DataSource.Retsinformation]: ({ metadata }) => (
    <RetsinformationSidebarContent
      metadata={metadata as RetsinformationMetadata}
    />
  ),
};

const DocumentMetadataTab: FC<DocumentMetadataTabProps> = ({
  legislation,
  workspaceDocument,
}) => {
  const legislationMetadata = useMemo<LegislationMetadata | undefined>(
    () => legislation?.metadata,
    [legislation]
  );

  // Workspace document: show tenant-defined metadata
  if (workspaceDocument) {
    return (
      <SidebarGroup>
        <SidebarGroupLabel className="px-2">
          Dokumentinformation
        </SidebarGroupLabel>
        <SidebarGroupContent className="px-2 py-2">
          <WorkspaceMetadataSidebarContent
            workspaceDocument={workspaceDocument}
          />
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  // Legislation: existing datasource-specific content
  if (!legislation) {
    return (
      <SidebarGroup>
        <SidebarGroupLabel className="px-2">
          Dokumentinformation
        </SidebarGroupLabel>
        <SidebarGroupContent className="px-2 py-2">
          <div className="text-center text-sm text-muted-foreground py-8">
            Ingen dokumentinformationer fundet
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  const renderContent = () => {
    if (!legislationMetadata) {
      return (
        <div className="text-center text-sm text-muted-foreground py-8">
          Ingen dokumentinformationer fundet
        </div>
      );
    }

    // Normalize the datasource using central config
    const normalizedDataSource = normalizeDataSource(legislation.datasource);

    // Look up the component in the registry
    if (normalizedDataSource) {
      const Component = SIDEBAR_CONTENT_REGISTRY[normalizedDataSource];
      if (Component) {
        return <Component metadata={legislationMetadata} />;
      }
    }

    // Fallback for unknown datasources
    return (
      <div className="text-center text-sm text-muted-foreground py-8">
        Ukendt datakilde: {legislation.datasource}
      </div>
    );
  };

  // Check if this is a Retsinformation source for the historical badge
  const isRetsinformation =
    normalizeDataSource(legislation.datasource) === DataSource.Retsinformation;

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="px-2">
        Dokumentinformation
      </SidebarGroupLabel>
      <SidebarGroupContent className="px-2 py-2">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className="text-xs max-w-full wrap-break-word whitespace-normal text-center"
          >
            {legislation.datasource}
          </Badge>
          {isRetsinformation &&
            (legislationMetadata as RetsinformationMetadata)?.isHistorical && (
              <Badge variant="destructive" className="text-xs">
                Historisk
              </Badge>
            )}
        </div>
        {renderContent()}
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

export default DocumentMetadataTab;
